import axios, { AxiosError } from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';

const BASE_URL = '/api';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// -- Token holder en memoria (no localStorage) --------------------------
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

// -- Hook para que el authStore reaccione al logout forzado -------------
type LogoutHandler = () => void;
let onForcedLogout: LogoutHandler | null = null;

export function setForcedLogoutHandler(handler: LogoutHandler | null): void {
  onForcedLogout = handler;
}

// -- Refresh handler (evita stampede) -----------------------------------
let refreshPromise: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  try {
    const res = await axios.post<{ accessToken: string }>(
      `${BASE_URL}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    return res.data.accessToken;
  } catch {
    return null;
  }
}

function refreshOnce(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      // Permite un nuevo refresh tras 100ms; evita ráfagas
      setTimeout(() => {
        refreshPromise = null;
      }, 100);
    });
  }
  return refreshPromise;
}

// -- Interceptors -------------------------------------------------------
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return config;
});

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;

    if (!original || status !== 401) {
      return Promise.reject(error);
    }

    // No reintentar el propio refresh ni login
    const url = original.url ?? '';
    if (url.includes('/auth/refresh') || url.includes('/auth/login')) {
      return Promise.reject(error);
    }

    if (original._retry) {
      // Ya reintentamos una vez; rendimos
      onForcedLogout?.();
      return Promise.reject(error);
    }

    original._retry = true;
    const newToken = await refreshOnce();
    if (!newToken) {
      onForcedLogout?.();
      return Promise.reject(error);
    }
    accessToken = newToken;
    original.headers.set('Authorization', `Bearer ${newToken}`);
    return apiClient.request(original);
  },
);
